import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, CheckCircle2, MessageCircle, Plus, Minus, Trash2, Calendar as CalendarIcon,
  Route, Clock, MapPin, PhoneCall,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { SHIFTING_TYPES, ITEM_CATALOG, TIME_SLOTS, VEHICLE_TYPES, suggestVehicle } from "@/lib/booking";
import { buildWhatsAppUrl, callUrl } from "@/lib/config";
import ProgressStepper from "@/components/book/ProgressStepper";
import AddressAutocomplete from "@/components/book/AddressAutocomplete";
import RouteMap from "@/components/book/RouteMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyState = {
  shifting_type: "",
  pickup: { address: "", floor: "", lift: "" },
  drop: { address: "", floor: "", lift: "" },
  distance_km: null, duration_min: null,
  items: [], // {name, quantity, category}
  vehicle_notes: "",
  description: "",
  shifting_date: null, time_slot: "",
  vehicle_type: "",
  name: "", mobile: "", email: "", whatsapp_preferred: true,
};

export default function BookPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const nav = useNavigate();

  const update = (patch) => setForm((s) => ({ ...s, ...patch }));

  // Auto-fetch route on both locations picked
  useEffect(() => {
    const p = form.pickup, d = form.drop;
    if (p?.lat && d?.lat) {
      axios.get(`${API}/route`, { params: { from_lat: p.lat, from_lon: p.lon, to_lat: d.lat, to_lon: d.lon } })
        .then(({ data }) => update({ distance_km: data.distance_km, duration_min: data.duration_min }))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup?.lat, form.drop?.lat]);

  useEffect(() => {
    if (!form.vehicle_type || form.vehicle_type === "Auto-suggest based on items") {
      update({ vehicle_type: suggestVehicle(form.items) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.items]);

  const addItem = (item, category) => {
    setForm((s) => {
      const existing = s.items.find((i) => i.name === item.name);
      if (existing) {
        return { ...s, items: s.items.map((i) => i.name === item.name ? { ...i, quantity: i.quantity + 1 } : i) };
      }
      return { ...s, items: [...s.items, { name: item.name, quantity: 1, category }] };
    });
  };
  const decItem = (name) => setForm((s) => ({
    ...s,
    items: s.items.map((i) => i.name === name ? { ...i, quantity: Math.max(0, i.quantity - 1) } : i).filter((i) => i.quantity > 0),
  }));
  const removeItem = (name) => setForm((s) => ({ ...s, items: s.items.filter((i) => i.name !== name) }));

  const canNext = () => {
    if (step === 1) return !!form.shifting_type;
    if (step === 2) return form.pickup?.address && form.drop?.address;
    if (step === 3) return form.shifting_type === "other" ? form.description.trim().length > 0 : form.items.length > 0 || form.description.trim().length > 0;
    if (step === 4) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
      const mobileOk = /^[0-9+\-\s()]{7,20}$/.test(form.mobile);
      return form.shifting_date && form.name.trim() && mobileOk && emailOk;
    }
    return true;
  };

  const next = () => {
    if (!canNext()) { toast.error("Please complete this step first"); return; }
    setStep((s) => Math.min(5, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        shifting_type: form.shifting_type,
        pickup: { address: form.pickup.address, lat: form.pickup.lat, lon: form.pickup.lon, floor: form.pickup.floor || "", lift: form.pickup.lift || "" },
        drop: { address: form.drop.address, lat: form.drop.lat, lon: form.drop.lon, floor: form.drop.floor || "", lift: form.drop.lift || "" },
        distance_km: form.distance_km, duration_min: form.duration_min,
        items: form.items,
        description: form.description,
        shifting_date: format(form.shifting_date, "yyyy-MM-dd"),
        time_slot: form.time_slot, vehicle_type: form.vehicle_type,
        name: form.name, mobile: form.mobile, email: form.email, whatsapp_preferred: form.whatsapp_preferred,
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      setSuccess(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Booking failed. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) return <SuccessScreen data={success} onNew={() => { setForm(emptyState); setStep(1); setSuccess(null); }} />;

  return (
    <div className="py-10 lg:py-16 bg-slate-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#081537] mb-2">Book a Private Consultation</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-bold text-[#081537]">Get your shifting booked in 5 easy steps</h1>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-blue-900/5 overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-slate-100 bg-slate-50/50">
            <ProgressStepper current={step} />
          </div>

          <div className="p-5 sm:p-8">
            {step === 1 && <StepType form={form} update={update} />}
            {step === 2 && <StepLocations form={form} update={update} />}
            {step === 3 && <StepItems form={form} update={update} addItem={addItem} decItem={decItem} removeItem={removeItem} />}
            {step === 4 && <StepDateContact form={form} update={update} />}
            {step === 5 && <StepConfirm form={form} />}

            <div className="mt-10 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
              <button
                type="button"
                onClick={step === 1 ? () => nav(-1) : back}
                data-testid="booking-back-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-slate-600 hover:text-[#081537] font-semibold text-sm px-4 py-2.5 rounded-full"
              >
                <ArrowLeft className="w-4 h-4" /> {step === 1 ? "Cancel" : "Back"}
              </button>

              {step < 5 ? (
                <Button
                  type="button"
                  onClick={next}
                  data-testid="booking-next-btn"
                  className="w-full sm:w-auto bg-[#081537] hover:bg-[#0F2152] text-white rounded-full h-12 px-8 font-semibold"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="booking-confirm-btn"
                  className="w-full sm:w-auto bg-[#081537] hover:bg-[#0F2152] text-white rounded-full h-12 px-8 font-semibold"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Booking...</> : <>Confirm Booking <CheckCircle2 className="w-4 h-4 ml-2" /></>}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-slate-500">
          Need help? <a href={buildWhatsAppUrl("Hi, I need help booking a shifting.")} target="_blank" rel="noreferrer" className="text-[#081537] font-semibold hover:underline">WhatsApp us</a> or <a href={callUrl} className="text-[#081537] font-semibold hover:underline">call now</a>.
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 1: Shifting Type ---------- */
function StepType({ form, update }) {
  return (
    <div>
      <h2 className="font-[Outfit] text-2xl font-bold text-[#081537]">What are you shifting?</h2>
      <p className="text-slate-600 mt-1 text-sm">Choose the type of move you&rsquo;re planning.</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {SHIFTING_TYPES.map(({ id, title, desc, Icon }) => {
          const active = form.shifting_type === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => update({ shifting_type: id })}
              data-testid={`shifting-type-${id}`}
              className={cn(
                "text-left p-5 rounded-2xl border-2 transition-all group",
                active ? "border-[#081537] bg-[#081537]/5" : "border-slate-200 hover:border-[#081537]/30 bg-white"
              )}
            >
              <div className={cn("w-12 h-12 rounded-xl grid place-items-center mb-3 transition-colors", active ? "bg-[#081537] text-white" : "bg-slate-100 text-[#081537] group-hover:bg-[#081537] group-hover:text-white")}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="font-[Outfit] text-lg font-semibold text-[#081537]">{title}</div>
              <div className="text-sm text-slate-500 mt-1">{desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Step 2: Locations ---------- */
function StepLocations({ form, update }) {
  return (
    <div>
      <h2 className="font-[Outfit] text-2xl font-bold text-[#081537]">Where&rsquo;s the move?</h2>
      <p className="text-slate-600 mt-1 text-sm">Search and pick pickup + drop addresses.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <AddressAutocomplete
            label="Pickup Address (From)"
            value={form.pickup}
            onChange={(loc) => update({ pickup: { ...form.pickup, ...loc } })}
            testId="pickup-address"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Floor</Label>
              <Input data-testid="pickup-floor" placeholder="e.g. 3" value={form.pickup.floor || ""} onChange={(e) => update({ pickup: { ...form.pickup, floor: e.target.value } })} />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Lift</Label>
              <Select value={form.pickup.lift || ""} onValueChange={(v) => update({ pickup: { ...form.pickup, lift: v } })}>
                <SelectTrigger data-testid="pickup-lift"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <AddressAutocomplete
            label="Drop Address (To)"
            value={form.drop}
            onChange={(loc) => update({ drop: { ...form.drop, ...loc } })}
            testId="drop-address"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Floor</Label>
              <Input data-testid="drop-floor" placeholder="e.g. 1" value={form.drop.floor || ""} onChange={(e) => update({ drop: { ...form.drop, floor: e.target.value } })} />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Lift</Label>
              <Select value={form.drop.lift || ""} onValueChange={(v) => update({ drop: { ...form.drop, lift: v } })}>
                <SelectTrigger data-testid="drop-lift"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <RouteMap pickup={form.pickup} drop={form.drop} />
        {form.distance_km != null && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
              <Route className="w-5 h-5 text-[#081537]" />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Distance</div>
                <div className="font-semibold text-[#081537]" data-testid="distance-value">{form.distance_km} km</div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#081537]" />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Est. Travel</div>
                <div className="font-semibold text-[#081537]" data-testid="duration-value">{Math.round(form.duration_min)} min</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Step 3: Items ---------- */
function StepItems({ form, update, addItem, decItem, removeItem }) {
  const catalog = ITEM_CATALOG[form.shifting_type] || [];
  const totalQty = form.items.reduce((s, i) => s + i.quantity, 0);
  return (
    <div>
      <h2 className="font-[Outfit] text-2xl font-bold text-[#081537]">What are we moving?</h2>
      <p className="text-slate-600 mt-1 text-sm">Add items by tapping the + button. You can add more details below.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-6">
          {catalog.map((group) => (
            <div key={group.label}>
              <h3 className="font-[Outfit] text-lg font-semibold text-[#081537] mb-3">{group.label}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {group.items.map(({ name, Icon }) => {
                  const found = form.items.find((i) => i.name === name);
                  return (
                    <div key={name} data-testid={`item-${name.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`} className={cn("border rounded-xl p-3 flex flex-col gap-2 transition-all", found ? "border-[#081537] bg-[#081537]/5" : "border-slate-200 bg-white")}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-[#081537]" />
                        <div className="text-sm font-medium text-slate-700 flex-1 truncate">{name}</div>
                      </div>
                      {found ? (
                        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg">
                          <button type="button" onClick={() => decItem(name)} className="w-8 h-8 grid place-items-center text-[#081537] hover:bg-slate-50 rounded-l-lg">
                            <Minus className="w-4 h-4" />
                          </button>
                          <div className="text-sm font-bold text-[#081537]">{found.quantity}</div>
                          <button type="button" onClick={() => addItem({ name }, group.label)} className="w-8 h-8 grid place-items-center text-[#081537] hover:bg-slate-50 rounded-r-lg">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => addItem({ name }, group.label)} className="text-xs font-semibold text-[#081537] hover:bg-[#081537] hover:text-white border border-[#081537] rounded-lg py-1.5 transition">
                          + Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {form.shifting_type === "vehicle" && (
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Make & model, condition notes</Label>
              <Textarea data-testid="vehicle-notes" rows={3} placeholder="e.g. Honda City 2020, running condition, minor scratch on rear bumper" value={form.vehicle_notes} onChange={(e) => update({ vehicle_notes: e.target.value })} />
            </div>
          )}

          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
              Additional Details / Special Instructions
            </Label>
            <Textarea
              data-testid="description-textarea"
              rows={4}
              placeholder="Fragile items, special handling, building restrictions, timing needs, etc."
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
            />
          </div>
        </div>

        {/* Cart Summary */}
        <div className="lg:sticky lg:top-24 h-fit bg-slate-50 rounded-2xl p-5 border border-slate-200" data-testid="cart-summary">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-[Outfit] font-semibold text-[#081537]">Your Items</h3>
            <span className="text-xs bg-[#081537] text-white px-2.5 py-1 rounded-full font-bold">{totalQty}</span>
          </div>
          {form.items.length === 0 ? (
            <p className="text-sm text-slate-500">No items added yet. Tap + on any item to start.</p>
          ) : (
            <ul className="space-y-2">
              {form.items.map((i) => (
                <li key={i.name} className="flex items-center justify-between bg-white rounded-lg p-2.5 border border-slate-100 text-sm">
                  <div>
                    <div className="font-medium text-slate-800">{i.name}</div>
                    <div className="text-xs text-slate-500">{i.category}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#081537]">×{i.quantity}</span>
                    <button type="button" onClick={() => removeItem(i.name)} data-testid={`remove-item-${i.name.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {form.shifting_type !== "other" && form.items.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-slate-500">
              Suggested vehicle: <span className="font-semibold text-[#081537]">{suggestVehicle(form.items)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 4: Date & Contact ---------- */
function StepDateContact({ form, update }) {
  return (
    <div>
      <h2 className="font-[Outfit] text-2xl font-bold text-[#081537]">When & how to reach you</h2>
      <p className="text-slate-600 mt-1 text-sm">Pick your preferred date and share contact details.</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Shifting Date *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="date-trigger"
                className="w-full flex items-center justify-between border border-slate-300 rounded-lg px-4 h-11 text-left bg-white text-slate-700 hover:border-slate-400"
              >
                {form.shifting_date ? format(form.shifting_date, "PPP") : <span className="text-slate-400">Pick a date</span>}
                <CalendarIcon className="w-4 h-4 text-slate-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" align="start">
              <Calendar mode="single" selected={form.shifting_date} onSelect={(d) => update({ shifting_date: d })} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Preferred Time Slot</Label>
          <Select value={form.time_slot} onValueChange={(v) => update({ time_slot: v })}>
            <SelectTrigger data-testid="time-slot"><SelectValue placeholder="Select time slot" /></SelectTrigger>
            <SelectContent>
              {TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Vehicle Type</Label>
          <Select value={form.vehicle_type || "Auto-suggest based on items"} onValueChange={(v) => update({ vehicle_type: v })}>
            <SelectTrigger data-testid="vehicle-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name *</Label>
          <Input data-testid="contact-name" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="John Doe" />
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone / WhatsApp *</Label>
          <Input data-testid="contact-mobile" inputMode="tel" value={form.mobile} onChange={(e) => update({ mobile: e.target.value })} placeholder="+91 98765 43210" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Email *</Label>
          <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="you@example.com" />
        </div>
        <div className="md:col-span-2 flex items-center gap-2">
          <Checkbox
            id="wa-pref"
            data-testid="whatsapp-preferred"
            checked={form.whatsapp_preferred}
            onCheckedChange={(v) => update({ whatsapp_preferred: !!v })}
          />
          <Label htmlFor="wa-pref" className="text-sm text-slate-700 cursor-pointer">I prefer to be contacted via WhatsApp</Label>
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 5: Confirm ---------- */
function StepConfirm({ form }) {
  return (
    <div>
      <h2 className="font-[Outfit] text-2xl font-bold text-[#081537]">Review your booking</h2>
      <p className="text-slate-600 mt-1 text-sm">Confirm the details below. You can go back to make changes.</p>

      <div className="mt-6 space-y-5">
        <Row label="Shifting Type" value={SHIFTING_TYPES.find((t) => t.id === form.shifting_type)?.title} />
        <Row label="From" value={<>
          <div className="font-medium text-[#081537]">{form.pickup.address}</div>
          <div className="text-xs text-slate-500">Floor: {form.pickup.floor || "-"} · Lift: {form.pickup.lift || "-"}</div>
        </>} />
        <Row label="To" value={<>
          <div className="font-medium text-[#081537]">{form.drop.address}</div>
          <div className="text-xs text-slate-500">Floor: {form.drop.floor || "-"} · Lift: {form.drop.lift || "-"}</div>
        </>} />
        {form.distance_km != null && (
          <Row label="Distance / ETA" value={<span>{form.distance_km} km · ~{Math.round(form.duration_min)} min</span>} />
        )}
        <Row label="Date & Time" value={<span>{form.shifting_date ? format(form.shifting_date, "PPP") : "-"} · {form.time_slot || "Flexible"}</span>} />
        <Row label="Vehicle" value={form.vehicle_type || "Auto-suggest"} />
        <Row label="Items" value={
          form.items.length === 0 ? <span className="text-slate-500">None</span> :
          <ul className="space-y-1">
            {form.items.map((i) => <li key={i.name} className="text-sm text-slate-700">{i.name} × {i.quantity} <span className="text-slate-400 text-xs">({i.category})</span></li>)}
          </ul>
        } />
        {form.description && <Row label="Notes" value={<span className="text-slate-700">{form.description}</span>} />}
        {form.vehicle_notes && <Row label="Vehicle Notes" value={<span className="text-slate-700">{form.vehicle_notes}</span>} />}
        <Row label="Contact" value={<>
          <div className="font-medium text-[#081537]">{form.name}</div>
          <div className="text-sm text-slate-600">{form.mobile} · {form.email}</div>
          {form.whatsapp_preferred && <div className="text-xs text-green-600 font-semibold mt-1">Prefers WhatsApp contact</div>}
        </>} />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pb-4 border-b border-slate-100">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="sm:col-span-3 text-sm text-slate-700">{value}</div>
    </div>
  );
}

/* ---------- Success screen ---------- */
function SuccessScreen({ data, onNew }) {
  return (
    <div className="py-20 bg-slate-50 min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#081537] text-white grid place-items-center mb-6" data-testid="booking-success">
          <CheckCircle2 className="w-11 h-11" />
        </div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-bold text-[#081537]">Booking Confirmed!</h1>
        <p className="mt-3 text-slate-600 text-lg">Our team will contact you shortly on WhatsApp / phone.</p>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your booking ID</div>
          <div className="mt-2 font-[Outfit] text-3xl font-bold text-[#081537]" data-testid="booking-id-display">{data.booking_id}</div>
          <p className="mt-3 text-sm text-slate-500">Save this ID for reference. We&rsquo;ve emailed a copy to you.</p>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={buildWhatsAppUrl(`Hi, I just booked shifting. My booking ID is ${data.booking_id}.`)}
            target="_blank"
            rel="noreferrer"
            data-testid="success-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-full px-6 py-3 font-semibold"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Us
          </a>
          <a href={callUrl} className="inline-flex items-center justify-center gap-2 border-2 border-[#081537] text-[#081537] hover:bg-[#081537] hover:text-white rounded-full px-6 py-3 font-semibold transition">
            <PhoneCall className="w-4 h-4" /> Call Now
          </a>
        </div>

        <button type="button" onClick={onNew} className="mt-6 text-sm font-semibold text-slate-500 hover:text-[#081537]">
          Book another move
        </button>
      </div>
    </div>
  );
}
